import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { ApiError } from '@/types/api'


// Use proxy in development, Cloudflare Worker proxy in production
const getApiBaseUrl = () => {
  // Always use relative proxy path - handled by Vite in dev and Cloudflare Worker in prod
  return '/api-proxy'
}

const API_BASE_URL = getApiBaseUrl()

const DROPSHIPPING_CLIENT_ID = import.meta.env.VITE_DROPSHIPPING_CLIENT_ID || '71'
const API_VERSION = '2.1.0'

// Log API configuration (only in development)
if (import.meta.env.DEV) {
  console.log('API Configuration:', {
    baseURL: API_BASE_URL,
    dropshippingClientId: DROPSHIPPING_CLIENT_ID,
    apiVersion: API_VERSION,
    fullURL: `${API_BASE_URL}/dropshipping-api/${DROPSHIPPING_CLIENT_ID}/${API_VERSION}`,
  })
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/dropshipping-api/${DROPSHIPPING_CLIENT_ID}/${API_VERSION}`,
  headers: {
    'Content-Type': 'application/json',
  },
  // Validate status to allow 401/403/404 to be handled in the interceptor
  validateStatus: (status) => status < 500,
})

// Request interceptor ... (unchanged)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Only add auth in development (when using Vite proxy)
    // Only check DEV environment for auth inclusion

    // Auth is handled by the Proxy (Vite in dev, Worker in prod)
    // We do NOT inject credentials here to keep them secure.
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Intercept 401 responses to prevent browser/app auth flows and return empty results instead
    if (response.status === 401) {
      console.error('API Authentication Failed (401). Please check VITE_API_USERNAME/PASSWORD in .env file.')

      const error: any = new Error('Authentication failed (401). Invalid Credentials.')
      error.status = 401
      error.code = 'AUTH_ERROR'
      error.response = response
      throw error
    }

    // Also intercept 403 Forbidden similarly
    if (response.status === 403) {
      console.error('API Permission Denied (403).')
      const error: any = new Error('Permission denied (403).')
      error.status = 403
      error.code = 'ACCESS_ERROR'
      error.response = response
      throw error
    }

    return response
  },
  (error: AxiosError<ApiError>) => {
    // Handle CORS/Network errors (they don't have a response)
    if (!error.response && error.request) {
      // ... (rest of the error handling remains the same)
      const errorMessage = (error as any).message || ''
      const isCorsError =
        error.code === 'ERR_NETWORK' ||
        errorMessage.includes('CORS') ||
        errorMessage.includes('blocked by CORS') ||
        errorMessage.includes('preflight')

      if (isCorsError) {
        console.error('CORS Error: Server does not allow requests from this origin')
        return Promise.reject({
          error: 'CORS_ERROR',
          traceId: undefined,
        } as ApiError)
      }

      console.error('Network Error:', errorMessage)
      return Promise.reject({
        error: 'NETWORK_ERROR',
        traceId: undefined,
      } as ApiError)
    }

    // Handle normal API response errors (500s, etc, since <500 corresponds to success in validateStatus now?)
    // Actually validateStatus: status < 500 means 2xx, 3xx, 4xx are treated as "success" (response handler called)
    // So this error handler is only for 5xx or network errors.

    // ... rest of error logic
    return Promise.reject({
      error: (error as any).message || 'An unexpected error occurred',
    } as ApiError)
  }
)

export default apiClient
